import React, { useRef, useState } from 'react'
import { useAuth } from '../../../context/useAuth'
import { useNls } from '../../../nls/nlsProvider'
import type { Workspace } from '../../../types/workspace'
import { Button, TextInput } from '../../Common'
import {
  confirmPhoneChangeCode,
  sendPhoneChangeCode,
} from '../../../services/contactIdentityChangeService'
import {
  linkMyCustomerRecord,
  refreshAuthIdToken,
} from '../../../services/opsBookingsService'
import { formatMemberBookingError } from './memberBookingsErrorMapper'

export interface MemberLinkBookingsCardProps {
  workspace: Workspace
  onLinked?: () => void
}

export function MemberLinkBookingsCard({
  workspace,
  onLinked,
}: MemberLinkBookingsCardProps): React.ReactElement {
  const { t } = useNls()
  const { currentUser } = useAuth()
  const recaptchaRef = useRef<HTMLDivElement | null>(null)

  const [busy, setBusy] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  // Phone verification state for users without a verified phone
  const [phoneDraft, setPhoneDraft] = useState('')
  const [verificationId, setVerificationId] = useState<string | null>(null)
  const [verificationCode, setVerificationCode] = useState('')

  const hasVerifiedPhone = Boolean(currentUser?.phoneNumber)

  const handleDirectLink = async () => {
    if (busy) return
    setBusy(true)
    setErrorMsg(null)
    setSuccessMsg(null)
    try {
      await refreshAuthIdToken(true)
      const res = await linkMyCustomerRecord({ workspaceId: workspace.id })
      if (res.linked) {
        setSuccessMsg(t('NLSMemberBookingsLinkSuccess'))
        onLinked?.()
      } else {
        setErrorMsg(t('NLSMemberBookingsNoPhoneRecord', { workspace: workspace.name }))
      }
    } catch (err: unknown) {
      setErrorMsg(
        formatMemberBookingError(err, t, {
          action: 'link',
          workspaceName: workspace.name,
        }),
      )
    } finally {
      setBusy(false)
    }
  }

  const handleSendCode = async () => {
    if (busy || !phoneDraft.trim() || !recaptchaRef.current) return
    setBusy(true)
    setErrorMsg(null)
    setSuccessMsg(null)
    try {
      const vId = await sendPhoneChangeCode(phoneDraft.trim(), recaptchaRef.current)
      setVerificationId(vId)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setErrorMsg(msg)
    } finally {
      setBusy(false)
    }
  }

  const handleConfirmCodeAndLink = async () => {
    if (busy || !verificationId || !verificationCode.trim()) return
    setBusy(true)
    setErrorMsg(null)
    setSuccessMsg(null)
    try {
      await confirmPhoneChangeCode(verificationId, verificationCode.trim())
      await refreshAuthIdToken(true)
      const res = await linkMyCustomerRecord({ workspaceId: workspace.id })
      if (res.linked) {
        setSuccessMsg(t('NLSMemberBookingsLinkSuccess'))
        onLinked?.()
      } else {
        setErrorMsg(t('NLSMemberBookingsNoPhoneRecord', { workspace: workspace.name }))
      }
    } catch (err: unknown) {
      setErrorMsg(
        formatMemberBookingError(err, t, {
          action: 'link',
          workspaceName: workspace.name,
        }),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="memberLinkBookingsCard" aria-label={t('NLSMemberBookingsLinkCardTitle')}>
      <h2 className="memberLinkBookingsCard__title">{t('NLSMemberBookingsLinkCardTitle')}</h2>
      <p className="memberLinkBookingsCard__desc">{t('NLSMemberBookingsLinkCardDesc')}</p>

      {errorMsg && (
        <div className="memberLinkBookingsCard__error" role="alert">
          {errorMsg}
        </div>
      )}

      {successMsg && (
        <div className="memberLinkBookingsCard__success" role="status">
          {successMsg}
        </div>
      )}

      {hasVerifiedPhone ? (
        <>
          <p className="memberLinkBookingsCard__phoneText">
            {currentUser?.phoneNumber}
          </p>
          <Button
            variant="primary"
            size="lg"
            disabled={busy}
            onClick={handleDirectLink}
          >
            {busy
              ? t('NLSMemberBookingsLinkingAction')
              : t('NLSMemberBookingsLinkAction')}
          </Button>
        </>
      ) : (
        <>
          {!verificationId ? (
            <div className="memberLinkBookingsCard__otpRow">
              <TextInput
                value={phoneDraft}
                onChange={setPhoneDraft}
                placeholder={t('NLSMemberBookingsPhonePlaceholder')}
                disabled={busy}
                aria-label={t('NLSMemberBookingsPhonePlaceholder')}
              />
              <Button
                variant="primary"
                size="md"
                disabled={busy || !phoneDraft.trim()}
                onClick={handleSendCode}
              >
                {busy
                  ? t('NLSMemberBookingsSendingCodeAction')
                  : t('NLSMemberBookingsSendCodeAction')}
              </Button>
            </div>
          ) : (
            <div className="memberLinkBookingsCard__otpRow">
              <TextInput
                value={verificationCode}
                onChange={setVerificationCode}
                placeholder={t('NLSMemberBookingsVerifyCodeTitle')}
                disabled={busy}
                aria-label={t('NLSMemberBookingsVerifyCodeTitle')}
              />
              <Button
                variant="primary"
                size="md"
                disabled={busy || !verificationCode.trim()}
                onClick={handleConfirmCodeAndLink}
              >
                {busy
                  ? t('NLSMemberBookingsVerifyingAction')
                  : t('NLSMemberBookingsVerifyCodeAction')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={handleSendCode}
              >
                {t('NLSMemberBookingsResendCodeAction')}
              </Button>
            </div>
          )}
        </>
      )}

      <div ref={recaptchaRef} className="memberLinkBookingsCard__recaptcha" />
    </section>
  )
}
